import { AddItemRecommendations } from 'models/AddItemResponse';
import styles from './styles.scss';
import { LoaderButton } from 'components/Button';
import Button from 'components/Shared/Button';
import {
  _DeliveryInformation as DeliveryInformation,
  MergedProps as DeliveryInformationProps
} from 'views/ProductPage/productInfo/deliveryInformation';
import { rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import Modal from 'components/Shared/Modal';
import cn from 'classnames';
import { useMemo, useRef } from 'react';
import { AddToCartPush } from 'views/ProductPage/addToCartPush';
import { useCartButtonIntersectionObserver } from 'views/ProductPage/addToCartPush/useCartButtonIntersectionObserver';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { IQuantity } from 'models/IQuantity';
import { MultiQuantity } from 'models/MultiQuantity';
import { validate } from 'views/ProductPage/validateAddToCart';
import { SamplesAndOfferSection } from 'views/ProductPage/productInfo/SamplesAndOfferSection/samplesAndOfferSection';
import { GtmAddToCartLocation } from 'store/googleTagManager/types';
import { AddToCartHover } from 'views/ProductPage/themes/BGH/addToCartHover/addToCartHover';

interface HoverState {
  isOpen: boolean;
  addItemRecommendations?: AddItemRecommendations;
  message?: string;
}

const validateSelectedOptions = (
  isFamily: boolean,
  customOptions: ConfiguratorBase['attributes'],
  iQuantity: IQuantity & Partial<MultiQuantity>,
  numberOfSelectedProducts: number
) => {
  const props = {
    isFamily,
    quantity: iQuantity,
    customOptions
  };

  const { isValid } = validate(props, numberOfSelectedProducts);

  return isValid;
};

interface CartComponentProps extends Pick<DeliveryInformationProps, 'meta' | 'showDeliveryInfo'> {
  errorModalOpen: boolean;
  closeErrorModal: () => void;
  isMulti: boolean;
  isDesktop?: boolean;
  quantity: JSX.Element;
  fullProductId: string;
  isDisabled: boolean;
  handleAdd: (btnLocation: GtmAddToCartLocation) => Promise<void>;
  closeHover: () => void;
  error?: string[];
  hoverState: HoverState;
  className?: string;
  inDrawer?: boolean;
  numberOfSelectedProducts: number;
  isFamily: boolean;
  quantityData: IQuantity & Partial<MultiQuantity>;
  customOptions: ConfiguratorBase['attributes'];
  isDisabledInfo?: string;
}

export const CartComponent = ({
  errorModalOpen,
  closeErrorModal,
  className,
  isMulti,
  isDesktop,
  inDrawer,
  quantity,
  fullProductId,
  isDisabled,
  handleAdd,
  closeHover,
  error,
  hoverState,
  meta,
  showDeliveryInfo,
  numberOfSelectedProducts,
  quantityData,
  isFamily,
  customOptions,
  isDisabledInfo: disabledInfoText
}: CartComponentProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const isVisible = useCartButtonIntersectionObserver(ref, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5
  });

  const hasRequiredData = useMemo(
    () => validateSelectedOptions(isFamily, customOptions, quantityData, numberOfSelectedProducts),
    [isFamily, customOptions, quantityData, numberOfSelectedProducts]
  );

  return (
    <>
      {isDisabled && disabledInfoText !== undefined && (
        <p className={styles.disabledInfoText}>{disabledInfoText}</p>
      )}
      <div className={className}>
        <AddToCartPush
          hasRequiredData={hasRequiredData}
          disabled={isDisabled}
          numberOfSelectedProducts={numberOfSelectedProducts}
          isVisible={!isVisible}
        >
          <LoaderButton
            disabled={isDisabled}
            onClick={() => handleAdd('sticky')}
            className={cn(styles.addToCartPushButton, { [styles.desktop]: isDesktop })}
            {...getQaDataAttr('addtocartdynamic')}
            appearance="quaternary"
          >
            {rm('pl_product_add_cart')}
          </LoaderButton>
        </AddToCartPush>
        <div
          ref={ref}
          className={cn(
            styles.addToCart,
            { [styles.column]: isMulti },
            { [styles.desktop]: isDesktop || inDrawer }
          )}
        >
          {isMulti && quantity}
          <LoaderButton
            disabled={isDisabled}
            onClick={() => handleAdd('image_component')}
            className={cn(styles.addToCartButton, { [styles.desktop]: isDesktop })}
            {...getQaDataAttr('addtocartstatic')}
            appearance="quaternary"
          >
            {rm('pl_product_add_cart')}
          </LoaderButton>
          {!isMulti && quantity}
        </div>
        {!inDrawer && (
          <div className={styles.samplesAndOfferSection}>
            <SamplesAndOfferSection parentProductId={fullProductId} />
          </div>
        )}
        <div>
          <DeliveryInformation meta={meta} showDeliveryInfo={showDeliveryInfo} />
        </div>
      </div>
      <Modal show={errorModalOpen} onClose={closeErrorModal}>
        <h3>{rm('dialogpopup_defaulttitle')}</h3>
        {error && error.map(e => <span key={e} dangerouslySetInnerHTML={{ __html: e }} />)}
        <Button onClick={closeErrorModal}>{rm('checkout_modal_confirm')}</Button>
      </Modal>
      <AddToCartHover {...hoverState} close={closeHover} />
    </>
  );
};
